import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAKAsList, selectUserData } from '~/features/dashboard/store/selectors';
import { useRouteMatch } from 'react-router-dom';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { axiosInstance } from '~/api/config/axios.config';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Button, TextField, Typography } from '@material-ui/core';
import css from './styles.module.scss';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import { getAKAsListings, updateAKAsListings } from '~/features/dashboard/store/actions.thunks';
// eslint-disable-next-line
import { AKAsList } from '~/models/contacts.dto';

export const AKAsListDrawer: React.FC<{ title: string; closeDrawer: () => void }> = ({ title = 'AKAs (Also known as)', closeDrawer }) => {
    const user = useSelector(selectUserData);
    const AKAsList = useSelector(getAKAsList);
    const match = useRouteMatch<{ creatorId: string }>();
    const creatorId = useMemo(() => Number(match.params.creatorId), [match.params.creatorId]);
    const [kontactCode, setKontactCode] = useState('');
    const dispatch = useThunkDispatch();

    let initialAKAsData = {
        kontactAKAID: '',
        projectId: creatorId,
        kontactCode: user?.contactCode || kontactCode,
        aka: '',
    };

    const { handleSubmit, control } = useForm<{ AKAsList: AKAsList[] }>({
        defaultValues: {
            AKAsList: AKAsList || [{ ...initialAKAsData }],
        },
    });

    const { fields: AKAsFields, append: AKAsAppend, remove: AKAsRemove } = useFieldArray({ control, name: 'AKAsList' });

    useEffect(() => {
        AKAsList.length < 1 && AKAsAppend(initialAKAsData);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        user && setKontactCode(user.contactCode);
    }, [user]);

    const deleteAKAs = async (i: any, values: any) => {
        AKAsRemove(i);
        values.kontactAKAID && (await axiosInstance.delete(`catalogue/projectcontact/aka/delete/${values.kontactAKAID}`));
        dispatch(getAKAsListings({ creatorId: values.projectId }));
    };

    const onSubmit = useCallback(
        (data: { AKAsList: AKAsList[] }) => {
            data.AKAsList.map((item, index) => {
                if (item.aka.length < 1) {
                    AKAsRemove(index);
                    axiosInstance.delete(`catalogue/projectcontact/aka/delete/${item.kontactAKAID}`);
                }
                return item;
            });
            dispatch(updateAKAsListings({ ...data, creatorId })).then(() => {
                closeDrawer();
                dispatch(getAKAsListings({ creatorId }));
            });
        },
        // eslint-disable-next-line
        [dispatch]
    );

    return (
        <div className={css.module}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Typography className={'bold'}>{title}</Typography>
                {AKAsFields.map((akas, index) => (
                    <div key={`item-${akas.kontactAKAID}-${akas.aka}`} className={css.itemsContainer}>
                        <Controller
                            render={props => <TextField fullWidth hidden value={props.value} onChange={data => props.onChange(data)} />}
                            defaultValue={akas.kontactAKAID}
                            name={`AKAsList[${index}].kontactAKAID`}
                            control={control}
                        />
                        <Controller
                            render={props => <TextField fullWidth hidden value={props.value} onChange={data => props.onChange(data)} />}
                            defaultValue={akas.kontactCode}
                            name={`AKAsList[${index}].kontactCode`}
                            control={control}
                        />
                        <Controller
                            render={props => (
                                <TextField fullWidth label={''} className={css.textInput} value={props.value} onChange={data => props.onChange(data)} />
                            )}
                            defaultValue={akas.aka}
                            name={`AKAsList[${index}].aka`}
                            control={control}
                        />
                        {index === 0 ? (
                            <AddCircleOutlineIcon className={css.addAKAs} onClick={() => AKAsAppend({ ...initialAKAsData })} />
                        ) : (
                            <CloseIcon className={css.deleteAKAs} onClick={() => deleteAKAs(index, akas)} />
                        )}
                    </div>
                ))}
                <Button className={css.btnSubmit} type={'submit'}>
                    Submit
                </Button>
            </form>
        </div>
    );
};
